import { useEffect, type RefObject } from "react";

/**
 * Explicitly triggers playback on mount. The declarative `autoplay`
 * attribute alone was confirmed (via on-device diagnostics) to sometimes
 * leave the video stuck at readyState=1 (metadata loaded, never playing)
 * on mobile Safari — an explicit `.play()` call is what actually starts it.
 *
 * iOS Low Power Mode (and some carrier/data-saver configurations) can
 * still reject that initial `.play()` outright, with no further event to
 * hook into — the video is left paused on its poster frame indefinitely.
 * Since a muted video is exempt from the interaction requirement once the
 * page has ANY interaction, retrying `.play()` on the first touch/click/
 * scroll anywhere on the page recovers playback in that case instead of
 * leaving the section permanently static.
 */
export default function useAutoplayVideo(ref: RefObject<HTMLVideoElement>) {
  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    video.muted = true;

    const tryPlay = () => video.play().catch(() => {});
    tryPlay();

    // `video.paused` flips to false synchronously as soon as .play() is
    // called, before the returned promise can reject — so a blocked
    // autoplay can't be detected here and the listeners are attached
    // unconditionally instead. Each retry is a no-op once playback is
    // already underway, and `once: true` means they self-remove after
    // firing regardless.
    const opts = { once: true, passive: true } as const;
    window.addEventListener("touchstart", tryPlay, opts);
    window.addEventListener("scroll", tryPlay, opts);
    window.addEventListener("click", tryPlay, opts);
    return () => {
      window.removeEventListener("touchstart", tryPlay);
      window.removeEventListener("scroll", tryPlay);
      window.removeEventListener("click", tryPlay);
    };
  }, [ref]);
}
